// Slime override (MOB special, M13.2). PURE (ENG-1).
//
// A gel cube that bounces toward the nearest player and, on death, splits into
// smaller slimes (4 → 2 → 1). Size lives in `variant`; damage scales with it and
// the smallest size (1) is harmless. Splitting is the `deathFor('slime')` hook.
import { MOB, SLIME } from '../../config';
import type { MobDef } from '../../registry/schemas';
import { halfOf, type Entity, type MobContext, type EntitySystem, type MobFrame } from '../entity';
import { wander, moveToward, tickSpeed, difficultyScale } from '../archetypes/shared';

export function slime(e: Entity, def: MobDef, ctx: MobContext): void {
  const { frame, sys } = ctx;
  const scale = difficultyScale(frame.difficulty);
  const near = scale > 0 ? sys.nearestPlayer(frame, e.x, e.y, e.z, def.followRange) : null;
  if (!near) { wander(e, ctx, def); return; }

  e.targetIdx = near.idx;
  const pl = frame.players[near.idx]!;
  // Bounce: steer toward the player, hopping on a cadence when grounded.
  moveToward(e, ctx, pl.x, pl.z, tickSpeed(def, 1));
  e.data.hopCd = (e.data.hopCd ?? 0) - 1;
  if (e.onGround && e.data.hopCd <= 0) { e.wishJump = true; e.data.hopCd = SLIME.JUMP_INTERVAL; }

  // Bite on contact — damage scales with size; the tiniest slime is harmless.
  const reach = halfOf(e, def) + 0.4 + MOB.MELEE.REACH_PAD;
  const flat = Math.hypot(pl.x - e.x, pl.z - e.z);
  const dmg = e.variant <= 1 ? 0 : e.variant;
  if (dmg > 0 && flat <= reach && Math.abs(pl.y - e.y) < 2 && e.attackCooldown === 0) {
    pl.hurt(dmg * scale, pl.x - e.x, pl.z - e.z);
    e.attackCooldown = MOB.MELEE.COOLDOWN;
  }
}

/** Death hook: a sized gel cube bigger than size 1 bursts into several half-size
 *  copies of ITSELF — `sys.spawn(e.type, …)`, so slime → slimes and magma_cube →
 *  magma_cubes off the one shared behaviour (MB.6 nether hostiles). */
export function slimeDeath(e: Entity, sys: EntitySystem, _frame: MobFrame): void {
  if (e.variant <= 1) return;
  const child = Math.floor(e.variant / 2);
  const n = SLIME.SPLIT_MIN + sys.rng.nextInt(SLIME.SPLIT_MAX - SLIME.SPLIT_MIN + 1);
  for (let i = 0; i < n; i++) {
    const ox = e.x + (sys.rng.nextFloat() - 0.5);
    const oz = e.z + (sys.rng.nextFloat() - 0.5);
    sys.spawn(e.type, ox, e.y, oz, { variant: child });
  }
}
